import { createReducer } from '@reduxjs/toolkit';
import {
  AdminUserResponse,
  AuthMethodResponse,
} from 'features/admin-users/api/types';
import { MetaType } from 'shared/ts-types/meta-type';
import { adminUsersActions } from 'features/admin-users/redux/admin-users.actions';

const initialState = {
  items: [] as AdminUserResponse[],
  meta: {
    itemCount: 0,
    totalItems: 0,
    itemsPerPage: 0,
    totalPages: 0,
    currentPage: 1,
  } as MetaType,
  loading: false as boolean,
  isLoaded: false as boolean,
  isSubmitted: false as boolean,
  error: {
    adminUsersList: [] as string[],
    postUser: [] as string[],
    getUser: [] as string[],
    deleteUser: [] as string[],
    authMethods: [] as string[],
    deleteUsers: [] as string[],
  },
  selectedUser: {} as AdminUserResponse,
  authMethods: [] as AuthMethodResponse[],
};

const adminUsers = createReducer(initialState, (builder) => {
  builder.addCase(adminUsersActions.clearState, () => {
    return {
      ...initialState,
    };
  });
  builder.addCase(adminUsersActions.getAdminUsersListRequest, (state) => {
    return {
      ...state,
      loading: true,
    };
  });
  builder.addCase(
    adminUsersActions.getAdminUsersListSuccess,
    (state, action) => {
      return {
        ...state,
        items:
          action.payload.meta.currentPage === 1
            ? [...action.payload.items]
            : [...state.items, ...action.payload.items],
        meta: action.payload.meta,
        loading: false,
        isLoaded: true,
      };
    }
  );
  builder.addCase(
    adminUsersActions.getAdminUsersListFailure,
    (state, action) => {
      return {
        ...state,
        loading: false,
        isLoaded: false,
        error: { ...state.error, adminUsersList: action.payload },
      };
    }
  );

  builder.addCase(adminUsersActions.createUserRequest, (state) => {
    return {
      ...state,
      loading: true,
    };
  });
  builder.addCase(adminUsersActions.createUserSuccess, (state, _) => {
    return {
      ...state,
      loading: false,
      isLoaded: true,
      isSubmitted: true,
    };
  });
  builder.addCase(adminUsersActions.createUserFailure, (state, action) => {
    return {
      ...state,
      loading: false,
      isLoaded: false,
      error: { ...state.error, postUser: action.payload },
    };
  });

  builder.addCase(adminUsersActions.getUserRequest, (state) => {
    return {
      ...state,
      loading: true,
    };
  });
  builder.addCase(adminUsersActions.getUserSuccess, (state, action) => {
    return {
      ...state,
      loading: false,
      isLoaded: true,
      selectedUser: action.payload,
    };
  });
  builder.addCase(adminUsersActions.getUserFailure, (state, action) => {
    return {
      ...state,
      loading: false,
      isLoaded: false,
      error: { ...state.error, getUser: action.payload },
    };
  });

  builder.addCase(adminUsersActions.updateUserRequest, (state) => {
    return {
      ...state,
      loading: true,
    };
  });
  builder.addCase(adminUsersActions.updateUserSuccess, (state, _) => {
    return {
      ...state,
      loading: false,
      isLoaded: true,
      isSubmitted: true,
    };
  });
  builder.addCase(adminUsersActions.updateUserFailure, (state, action) => {
    return {
      ...state,
      loading: false,
      isLoaded: false,
      error: { ...state.error, postUser: action.payload },
    };
  });

  builder.addCase(adminUsersActions.deleteUserRequest, (state) => {
    return {
      ...state,
      loading: true,
    };
  });
  builder.addCase(adminUsersActions.deleteUserSuccess, (state, action) => {
    return {
      ...state,
      loading: false,
      items: state.items.filter((item) => item.id !== action.payload),
    };
  });
  builder.addCase(adminUsersActions.deleteUserFailure, (state, action) => {
    return {
      ...state,
      loading: false,
      isLoaded: false,
      error: { ...state.error, deleteUser: action.payload },
    };
  });

  builder.addCase(adminUsersActions.getAuthMethodsRequest, (state) => {
    return {
      ...state,
      loading: true,
    };
  });
  builder.addCase(adminUsersActions.getAuthMethodsSuccess, (state, action) => {
    return {
      ...state,
      authMethods: [...action.payload.items],
      loading: false,
      isLoaded: true,
    };
  });
  builder.addCase(adminUsersActions.getAuthMethodsFailure, (state, action) => {
    return {
      ...state,
      loading: false,
      isLoaded: false,
      error: { ...state.error, authMethods: action.payload },
    };
  });
  builder.addCase(adminUsersActions.deleteUsersRequest, (state) => {
    return {
      ...state,
      loading: true,
    };
  });
  builder.addCase(adminUsersActions.deleteUsersSuccess, (state, action) => {
    return {
      ...state,
      loading: false,
      items: state.items.filter((item) => !action.payload.includes(item.id)),
    };
  });
  builder.addCase(adminUsersActions.deleteUsersFailure, (state, action) => {
    return {
      ...state,
      error: { ...state.error, deleteUsers: action.payload },
    };
  });
});

export default adminUsers;
